/**
 * Shared `resolveSelectedRow` helper (Crucible #687, design
 * `docs/c3-agents-panel-design.md` §3.2).
 *
 * Pure, unit-testable. Both the banner (C2, after the B2 migration
 * lands #688) and the panel (C3) consume this so the two surfaces
 * always agree about which agent is the spotlight. The function is
 * intentionally free of React imports — call it from a render path,
 * a memo selector, or a comparator without any wrapper.
 *
 * Four-step fallback chain (verbatim from C2 banner-local definition
 * at `ActivityBanner.tsx:122-164`):
 *
 *   1. Pinned agent — if a pin is active AND its `sessionId` matches
 *      the snapshot's `sessionId` AND its `agentId` is in
 *      `byAgentId`, use that row. The `sessionId` guard prevents a
 *      session-A pin from resurrecting against session-B's reserved
 *      `__main__` row on the first push (Codex round-2 HIGH on the
 *      C2 design).
 *   2. Current selection — if `byAgentId[currentSelection]` exists,
 *      use that.
 *   3. Orchestrator — fall back to `byAgentId['__main__']` if
 *      present.
 *   4. Newest running — pick the row with `status: 'running'` and
 *      the maximum `startedAt`.
 *   5. None — return `null` so consumers render the generic fallback
 *      copy.
 *
 * Returned as `AgentActivity | null` so callers can use the truthy
 * check + microcopy fallback uniformly. Distinct from
 * `currentSelection` (a string id) — this helper resolves the id (or
 * its fallbacks) to the actual row.
 *
 * Flag-gating: the function does not gate — it's a pure data
 * transform. Consumers are responsible for calling it only inside a
 * flag-gated render path; see `pin-store.ts` for the convention.
 *
 * British spelling throughout (behaviour, centre, colour, etc.).
 */

import type {
  ActivitySnapshot,
  AgentActivity,
  AgentStreamChunk,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { AgentRow, MainAgentRow } from '../chat/state';
import type { Pin } from './pin-store';

/**
 * Reserved orchestrator agent id (mirrors the wire's
 * `ActivitySnapshot.byAgentId['__main__']` convention). Single-sourced
 * so the panel + selectors never disagree on the literal.
 */
export const MAIN_AGENT_ID = '__main__';

/**
 * Pure selector — routes an agentId to its transcript chunk buffer
 * (design `docs/c3-agents-panel-design.md` §9 "Transcript-selector
 * contract"). The orchestrator (`__main__`) reads from the chat
 * reducer's `mainAgent` slice; every other agentId reads from the
 * `agents` map. Returns empty arrays + zero `chunksSeenTotal` when
 * the relevant slice entry is missing — covers the turn-boundary drift
 * window (design §2 "Turn-boundary drift") during which the reducer
 * has already cleared its slices but the registry hasn't yet pushed
 * the new turn's snapshot.
 *
 * Kept here rather than at the panel module so banner / drawer / panel
 * consumers can share one canonical helper. Pure — no React, no IPC.
 */
export function selectTranscriptBuffer(
  agentId: string,
  chatAgents: ReadonlyMap<string, AgentRow>,
  chatMainAgent: MainAgentRow | null,
): { chunks: readonly AgentStreamChunk[]; chunksSeenTotal: number } {
  if (agentId === MAIN_AGENT_ID) {
    return {
      chunks: chatMainAgent?.recentChunks ?? EMPTY_CHUNKS,
      chunksSeenTotal: chatMainAgent?.chunksSeenTotal ?? 0,
    };
  }
  const row = chatAgents.get(agentId);
  return {
    chunks: row?.recentChunks ?? EMPTY_CHUNKS,
    chunksSeenTotal: row?.chunksSeenTotal ?? 0,
  };
}

/**
 * Frozen empty array used by `selectTranscriptBuffer`'s missing-slice
 * branch. Stable reference across calls so a memoised row's chunks
 * prop equality short-circuits to `true` while the slice is absent —
 * critical for the turn-boundary drift window where the reducer has
 * cleared the slice but the snapshot still carries the old row.
 */
const EMPTY_CHUNKS: readonly AgentStreamChunk[] = Object.freeze([]);

export function resolveSelectedRow(
  snapshot: ActivitySnapshot,
  pin: Pin | null,
): AgentActivity | null {
  const byAgentId = snapshot.byAgentId;

  // Step 1 — pinned agent (when pin is live, scoped to THIS
  // session, AND present in map). The `sessionId` guard prevents a
  // session-A pin from resurrecting in session B when both sessions
  // happen to use the same reserved id (e.g. `__main__`) — without
  // it, the first render of session B would briefly resolve the old
  // pin to B's new `__main__` row before any drop-pin effect ran.
  if (pin !== null && pin.sessionId === snapshot.sessionId) {
    const pinned = byAgentId[pin.agentId];
    if (pinned !== undefined) return pinned;
    // Pin points at an agent that no longer exists — fall through
    // silently. The consumer's drop-pin effect (B2 banner / C3
    // panel) clears the dangling pin; in the meantime the
    // heuristic picks the next-best row.
  }

  // Step 2 — current selection from the registry.
  const sel = byAgentId[snapshot.currentSelection];
  if (sel !== undefined) return sel;

  // Step 3 — orchestrator row by reserved id.
  const main = byAgentId['__main__'];
  if (main !== undefined) return main;

  // Step 4 — newest running specialist.
  let newest: AgentActivity | null = null;
  for (const row of Object.values(byAgentId)) {
    if (row.status !== 'running') continue;
    if (newest === null || row.startedAt > newest.startedAt) {
      newest = row;
    }
  }
  if (newest !== null) return newest;

  // Step 5 — nothing to show.
  return null;
}
